import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  Send, Trash2, Bot, Plus, BookOpen, X, ChevronDown, Check, Sparkles, 
  Camera, Upload, Image, XCircle, History, FolderPlus, Folder, MessageSquare, 
  MoreVertical, Edit2,
  // Model icons
  Brain, Wand2, Scale, HeartPulse, Code2, Palette, TrendingUp, LineChart,
  GraduationCap, Briefcase, Globe, Lightbulb, Wrench, Shield
} from 'lucide-react';

// Icon mapping for models
const MODEL_ICONS = {
  sage: Brain,
  general: Brain,
  prompting: Wand2,
  legal: Scale,
  medical: HeartPulse,
  coding: Code2,
  code: Code2,
  creative: Palette,
  reseller: TrendingUp,
  resale: TrendingUp,
  finance: LineChart,
  education: GraduationCap,
  business: Briefcase,
  travel: Globe,
  ideas: Lightbulb,
  technical: Wrench,
  security: Shield,
  default: Bot
};

// Get icon component for a model
const getModelIcon = (model) => {
  if (!model) return Bot;
  const id = model.id?.toLowerCase() || '';
  const name = model.name?.toLowerCase() || '';
  
  // Check id first, then name
  for (const [key, IconComponent] of Object.entries(MODEL_ICONS)) {
    if (id.includes(key) || name.includes(key)) {
      return IconComponent;
    }
  }
  return Bot;
};

// Styled icon component with glow
const ModelIcon = ({ model, size = 'md', className = '' }) => {
  const IconComponent = getModelIcon(model);
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <div className={`relative flex items-center justify-center ${className}`}>
      <div className="absolute inset-0 bg-[#00d4ff]/20 blur-md rounded-full" />
      <IconComponent 
        className={`${sizeClasses[size]} text-[#00d4ff] relative z-10`}
        style={{ filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.6))' }}
      />
    </div>
  );
};

// Default models (fallback if Supabase table doesn't exist)
const DEFAULT_MODELS = [
  { id: 'sage', name: 'Sage', description: 'Your intelligent all-purpose AI assistant.', system_prompt: 'You are Sage, a helpful and knowledgeable AI assistant. Be concise, accurate, and friendly.', is_default: true },
  { id: 'prompting', name: 'Prompting Expert', description: 'Crafting effective AI prompts.', system_prompt: 'You are Sage in Prompting Expert mode. Help users craft effective prompts for AI systems.' },
  { id: 'legal', name: 'Legal Advisor', description: 'General legal information.', system_prompt: 'You are Sage in Legal Advisor mode. Provide general legal information. Remind users you cannot provide legal advice.' },
  { id: 'medical', name: 'Medical Guide', description: 'Health information and guidance.', system_prompt: 'You are Sage in Medical Guide mode. Provide health information. Remind users you cannot provide medical advice.' },
  { id: 'coding', name: 'Code Assistant', description: 'Expert programming help.', system_prompt: 'You are Sage in Code Assistant mode. You are an expert programmer. Help with code, debugging, and best practices.' },
  { id: 'creative', name: 'Creative Writer', description: 'Creative writing and content.', system_prompt: 'You are Sage in Creative Writer mode. Help with creative writing, storytelling, and content creation.' }
];

export default function Chat({ navigate }) {
  const { user, profile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [skills, setSkills] = useState([]);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [newSkill, setNewSkill] = useState({ title: '', content: '' });
  const [models, setModels] = useState(DEFAULT_MODELS);
  // Default to General model
  const [selectedModel, setSelectedModel] = useState(
    DEFAULT_MODELS.find(m => m.name === 'General') || DEFAULT_MODELS[0]
  );
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [promptToSave, setPromptToSave] = useState(null);
  const [showResellerPrompt, setShowResellerPrompt] = useState(false);
  const [premiumModels, setPremiumModels] = useState({}); // Track which models use premium (Gemini)
  
  // Chat History & Projects
  const [showHistory, setShowHistory] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [historyTab, setHistoryTab] = useState('chats'); // 'chats' or 'projects'
  
  // Reseller Expert - Image capture states
  const [showImageCapture, setShowImageCapture] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isUsingCamera, setIsUsingCamera] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Check if Reseller model is selected
  const isResellerMode = selectedModel?.name?.toLowerCase().includes('reseller') || 
                         selectedModel?.name?.toLowerCase().includes('resale');

  useEffect(() => {
    fetchSkills();
    fetchModels();
    loadChatHistory();
    loadProjects();
    
    // Check for pending prompt from Prompt Vault
    const pendingPrompt = sessionStorage.getItem('sage_pending_prompt');
    if (pendingPrompt) {
      setInput(pendingPrompt);
      sessionStorage.removeItem('sage_pending_prompt');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, []);

  // Chat History Functions
  const loadChatHistory = () => {
    const sessions = JSON.parse(localStorage.getItem(`sage_chat_sessions_${user?.id}`) || '[]');
    setChatSessions(sessions.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
  };

  const loadProjects = () => {
    const stored = JSON.parse(localStorage.getItem(`sage_projects_${user?.id}`) || '[]');
    setProjects(stored);
  };

  const saveCurrentChat = () => {
    if (messages.length === 0) return;
    
    const sessionId = currentSessionId || `chat_${Date.now()}`;
    const title = messages[0]?.content?.slice(0, 50) || 'New Chat';
    
    const session = {
      id: sessionId,
      title,
      messages,
      model: selectedModel?.name,
      projectId: selectedProject,
      createdAt: currentSessionId ? 
        chatSessions.find(s => s.id === sessionId)?.createdAt : 
        new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const sessions = JSON.parse(localStorage.getItem(`sage_chat_sessions_${user?.id}`) || '[]');
    const existingIndex = sessions.findIndex(s => s.id === sessionId);
    
    if (existingIndex >= 0) {
      sessions[existingIndex] = session;
    } else {
      sessions.unshift(session);
    }
    
    localStorage.setItem(`sage_chat_sessions_${user?.id}`, JSON.stringify(sessions));
    setChatSessions(sessions.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
    setCurrentSessionId(sessionId);
  };

  const loadChatSession = (session) => {
    setMessages(session.messages || []);
    setCurrentSessionId(session.id);
    setSelectedProject(session.projectId || null);
    // Find and set the model
    const model = models.find(m => m.name === session.model);
    if (model) setSelectedModel(model);
    setShowHistory(false);
  };

  const deleteChatSession = (sessionId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this chat?')) return;
    
    const sessions = chatSessions.filter(s => s.id !== sessionId);
    localStorage.setItem(`sage_chat_sessions_${user?.id}`, JSON.stringify(sessions));
    setChatSessions(sessions);
    
    if (currentSessionId === sessionId) {
      setMessages([]);
      setCurrentSessionId(null);
    }
  };

  const createProject = () => {
    if (!newProjectName.trim()) return;
    
    const project = {
      id: `project_${Date.now()}`,
      name: newProjectName.trim(),
      createdAt: new Date().toISOString()
    };
    
    const updated = [...projects, project];
    localStorage.setItem(`sage_projects_${user?.id}`, JSON.stringify(updated));
    setProjects(updated);
    setNewProjectName('');
    setShowNewProject(false);
  };

  const deleteProject = (projectId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this project? Chats will be unassigned, not deleted.')) return;
    
    // Remove project
    const updatedProjects = projects.filter(p => p.id !== projectId);
    localStorage.setItem(`sage_projects_${user?.id}`, JSON.stringify(updatedProjects));
    setProjects(updatedProjects);
    
    // Unassign chats from this project
    const sessions = chatSessions.map(s => 
      s.projectId === projectId ? { ...s, projectId: null } : s
    );
    localStorage.setItem(`sage_chat_sessions_${user?.id}`, JSON.stringify(sessions));
    setChatSessions(sessions);
    
    if (selectedProject === projectId) setSelectedProject(null);
  };

  const assignChatToProject = (sessionId, projectId) => {
    const sessions = chatSessions.map(s => 
      s.id === sessionId ? { ...s, projectId } : s
    );
    localStorage.setItem(`sage_chat_sessions_${user?.id}`, JSON.stringify(sessions));
    setChatSessions(sessions);
  };

  const startNewChat = () => {
    saveCurrentChat(); // Save current before starting new
    setMessages([]);
    setCurrentSessionId(null);
    setSelectedProject(null);
    setShowHistory(false);
  };

  const getProjectChats = (projectId) => {
    return chatSessions.filter(s => s.projectId === projectId);
  };

  const fetchModels = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_models')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      
      if (!error && data && data.length > 0) {
        setModels(data);
        // Default to "General" model (is_default = true) or first model
        const defaultModel = data.find(m => m.is_default === true) || 
                            data.find(m => m.name === 'General') || 
                            data[0];
        setSelectedModel(defaultModel);
      } else {
        // Fallback to localStorage or defaults
        const storedModels = JSON.parse(localStorage.getItem('sage_ai_models') || 'null');
        if (storedModels && storedModels.length > 0) {
          setModels(storedModels);
          const defaultModel = storedModels.find(m => m.is_default === true) || 
                              storedModels.find(m => m.name === 'General') || 
                              storedModels[0];
          setSelectedModel(defaultModel);
        }
      }
    } catch (error) {
      console.log('Using default models');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Camera and Image Functions for Reseller Mode
  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      setCameraStream(stream);
      setIsUsingCamera(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Unable to access camera. Please check permissions or upload an image instead.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsUsingCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      stopCamera();
      setShowImageCapture(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image too large. Please use an image under 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result);
        setShowImageCapture(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setCapturedImage(null);
  };

  const openImageCapture = () => {
    setShowImageCapture(true);
    setCameraError(null);
  };

  const closeImageCapture = () => {
    stopCamera();
    setShowImageCapture(false);
  };

  // Switch to Reseller mode and open camera
  const activateResellerCamera = () => {
    // Find Reseller model
    const resellerModel = models.find(m => 
      m.name?.toLowerCase().includes('reseller') || m.name?.toLowerCase().includes('resale')
    );
    
    if (resellerModel) {
      setSelectedModel(resellerModel);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Switched to **${resellerModel.name}** mode. 📸 Opening camera for item analysis...`
      }]);
    }
    
    setShowResellerPrompt(false);
    setTimeout(() => setShowImageCapture(true), 300);
  };

  const dismissResellerPrompt = () => {
    setShowResellerPrompt(false);
  };

  const fetchSkills = async () => {
    try {
      // Try Supabase first
      const { data, error } = await supabase
        .from('knowledge_base')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (!error && data) {
        setSkills(data);
      } else {
        // Fallback to localStorage
        const localSkills = JSON.parse(localStorage.getItem(`sage_skills_${user.id}`) || '[]');
        setSkills(localSkills);
      }
    } catch (error) {
      const localSkills = JSON.parse(localStorage.getItem(`sage_skills_${user.id}`) || '[]');
      setSkills(localSkills);
    }
  };

  const addSkill = async () => {
    if (!newSkill.title.trim() || !newSkill.content.trim()) return;

    const skill = {
      id: Date.now().toString(),
      user_id: user.id,
      title: newSkill.title.trim(),
      content: newSkill.content.trim(),
      created_at: new Date().toISOString()
    };

    try {
      // Try Supabase
      const { data, error } = await supabase
        .from('knowledge_base')
        .insert(skill)
        .select()
        .single();
      
      if (!error && data) {
        setSkills([data, ...skills]);
      } else {
        // Fallback to localStorage
        const updatedSkills = [skill, ...skills];
        localStorage.setItem(`sage_skills_${user.id}`, JSON.stringify(updatedSkills));
        setSkills(updatedSkills);
      }
    } catch (error) {
      const updatedSkills = [skill, ...skills];
      localStorage.setItem(`sage_skills_${user.id}`, JSON.stringify(updatedSkills));
      setSkills(updatedSkills);
    }

    setNewSkill({ title: '', content: '' });
    setShowAddSkill(false);
    
    // Confirm to user
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `I've learned a new skill: **${skill.title}**. I'll use this knowledge in our conversations.`
    }]);
  };

  const buildSystemPromptWithSkills = () => {
    let basePrompt = selectedModel?.system_prompt || 'You are Sage, a helpful AI assistant.';
    
    if (skills.length > 0) {
      basePrompt += '\n\nUser has taught you the following skills and knowledge:\n';
      skills.forEach(skill => {
        basePrompt += `\n### ${skill.title}\n${skill.content}\n`;
      });
      basePrompt += '\nUse this knowledge when relevant to answer questions.';
    }
    return basePrompt;
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    // Check if user wants to add a skill via chat
    const teachPatterns = [
      /^teach\s+(?:sage\s+)?(?:that\s+)?(.+)/i,
      /^remember\s+(?:that\s+)?(.+)/i,
      /^learn\s+(?:that\s+)?(.+)/i,
      /^add\s+skill[:\s]+(.+)/i
    ];

    for (const pattern of teachPatterns) {
      const match = input.match(pattern);
      if (match) {
        const content = match[1].trim();
        const title = content.split(/[.!?]/)[0].substring(0, 50) || 'New Knowledge';
        
        setMessages(prev => [...prev, { role: 'user', content: input.trim() }]);
        setInput('');
        
        const skill = {
          id: Date.now().toString(),
          user_id: user.id,
          title: title,
          content: content,
          created_at: new Date().toISOString()
        };
        
        try {
          await supabase.from('knowledge_base').insert(skill);
        } catch (e) {
          // Use localStorage
        }
        
        const updatedSkills = [skill, ...skills];
        localStorage.setItem(`sage_skills_${user.id}`, JSON.stringify(updatedSkills));
        setSkills(updatedSkills);
        
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Got it! I've learned: **${title}**\n\nI'll remember this and use it in our future conversations.`
        }]);
        return;
      }
    }

    const userMessage = { 
      role: 'user', 
      content: input.trim(),
      image: capturedImage || null // Include image if present
    };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    const imageToSend = capturedImage; // Store before clearing
    setCapturedImage(null); // Clear image after sending
    setLoading(true);
    
    // Keep focus on input field
    setTimeout(() => inputRef.current?.focus(), 10);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Build system prompt with model + skills context
      const systemPrompt = buildSystemPromptWithSkills();
      
      // Determine which LLM to use:
      // - Premium mode enabled → gemini
      // - Image attached → gemini (needs vision)
      // - Sage/General mode → gemini (smarter routing)
      // - Other expert modes → rhino (faster local)
      const isPremiumEnabled = premiumModels[selectedModel?.id] === true;
      const isSageMode = selectedModel?.name === 'Sage' || selectedModel?.name === 'General' || selectedModel?.is_default;
      const hasImage = !!imageToSend;
      const llmModel = (isPremiumEnabled || hasImage || isSageMode) ? 'gemini' : 'rhino';
      
      const response = await fetch(`${backendUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: llmModel, // 'rhino' or 'gemini'
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          userEmail: user?.email,
          systemPrompt: systemPrompt,
          modelName: selectedModel?.name || 'General',
          image: imageToSend // Send image to backend for vision analysis
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      const aiMessage = { role: 'assistant', content: data.reply || 'No response' };
      setMessages([...newMessages, aiMessage]);
      
      // If using Prompting Expert, offer to save the prompt
      const isPromptingExpert = selectedModel?.name?.toLowerCase().includes('prompting') || 
                                selectedModel?.id === 'prompting';
      if (isPromptingExpert && data.reply && data.reply.length > 50) {
        setPromptToSave(data.reply);
      }
      
      // If using General model and AI detects resale topic, offer camera feature
      const isGeneralModel = selectedModel?.name === 'General' || selectedModel?.is_default;
      if (isGeneralModel && data.reply) {
        const replyLower = data.reply.toLowerCase();
        const resaleKeywords = ['reseller', 'resale', 'flip', 'valuation', 'worth', 'sell this', 
                               'price check', 'how much is', 'ebay', 'marketplace', 'mercari', 
                               'poshmark', 'what is it worth', 'reselling'];
        const containsResale = resaleKeywords.some(keyword => replyLower.includes(keyword));
        
        if (containsResale && !isResellerMode) {
          setShowResellerPrompt(true);
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { role: 'assistant', content: 'Connection error. Please try again.' };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setLoading(false);
      // Ensure input stays focused after response
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    if (messages.length > 0) {
      saveCurrentChat(); // Save before clearing
    }
    setMessages([]);
    setPromptToSave(null);
    setCurrentSessionId(null);
  };

  // Save prompt to Prompt Vault
  const saveToPromptVault = async (content) => {
    const title = `Prompt - ${new Date().toLocaleDateString()}`;
    
    try {
      await supabase.from('prompt_vault').insert({
        user_id: user.id,
        title: title,
        content: content,
        category: 'general',
        created_at: new Date().toISOString()
      });
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✅ Saved to your **Prompt Vault**! You can find it in Tools → Prompts.`
      }]);
    } catch (error) {
      // Fallback to localStorage
      const prompts = JSON.parse(localStorage.getItem(`sage_prompts_${user.id}`) || '[]');
      prompts.unshift({ id: Date.now(), user_id: user.id, title, content, category: 'general', created_at: new Date().toISOString() });
      localStorage.setItem(`sage_prompts_${user.id}`, JSON.stringify(prompts));
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✅ Saved to your **Prompt Vault**!`
      }]);
    }
    
    setPromptToSave(null);
  };

  const dismissPromptSave = () => {
    setPromptToSave(null);
  };

  const formatMessage = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#00d4ff]">$1</strong>')
      .replace(/`(.*?)`/g, '<code class="bg-[#1a1a24] px-1.5 py-0.5 text-[#00ff88] text-xs">$1</code>')
      .replace(/\n/g, '<br />');
  };

  return (
    <div className="flex flex-col h-screen bg-[#08080c]" data-testid="chat-screen">
      {/* Custom Chat Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#1a1a24]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center bg-[#00d4ff]/5">
            <ModelIcon model={selectedModel} size="md" />
          </div>
          <div>
            {/* Model Selector */}
            <button
              onClick={() => setShowModelPicker(!showModelPicker)}
              className="flex items-center gap-1.5 hover:bg-[#111118] px-2 py-1 -ml-2 transition-colors"
              data-testid="model-selector-btn"
            >
              <h1 className="font-mono text-xs font-medium text-[#e8e8f0] tracking-wide">
                {selectedModel?.name || 'SAGE'}
              </h1>
              {premiumModels[selectedModel?.id] && (
                <span className="px-1.5 py-0.5 bg-gradient-to-r from-[#ffd700]/20 to-[#ff8c00]/20 border border-[#ffd700]/50 text-[#ffd700] text-[8px] font-mono uppercase">
                  Premium
                </span>
              )}
              <ChevronDown className={`w-3 h-3 text-[#4a4a60] transition-transform ${showModelPicker ? 'rotate-180' : ''}`} />
            </button>
            <p className="text-[9px] text-[#00ff88] font-mono">
              {skills.length > 0 ? `${skills.length} skills learned` : 'online'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistory(true)}
            className="p-2 hover:bg-[#111118] transition-colors border border-transparent hover:border-[#00d4ff]/30 group"
            title="Chat History"
            data-testid="history-btn"
          >
            <History className="w-4 h-4 text-[#4a4a60] group-hover:text-[#00d4ff]" />
          </button>
          <button
            onClick={startNewChat}
            className="p-2 hover:bg-[#111118] transition-colors border border-transparent hover:border-[#00d4ff]/30 group"
            title="New Chat"
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4 text-[#4a4a60] group-hover:text-[#00d4ff]" />
          </button>
          <button
            onClick={clearChat}
            className="p-2 hover:bg-[#111118] transition-colors border border-transparent hover:border-[#1a1a24]"
            title="Clear & Save"
            data-testid="clear-chat-btn"
          >
            <Trash2 className="w-4 h-4 text-[#4a4a60]" />
          </button>
        </div>
      </div>

      {/* Chat History Panel */}
      {showHistory && (
        <div className="fixed inset-0 z-50 bg-[#08080c]" data-testid="history-panel">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#1a1a24]">
              <div className="flex items-center gap-3">
                <History className="w-4 h-4 text-[#00d4ff]" />
                <h2 className="font-mono text-xs text-[#00d4ff] uppercase tracking-wider">Chat History</h2>
              </div>
              <button
                onClick={() => setShowHistory(false)}
                className="p-2 hover:bg-[#111118]"
              >
                <X className="w-4 h-4 text-[#4a4a60]" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-[#1a1a24]">
              <button
                onClick={() => setHistoryTab('chats')}
                className={`flex-1 py-3 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                  historyTab === 'chats' 
                    ? 'text-[#00d4ff] border-b-2 border-[#00d4ff]' 
                    : 'text-[#4a4a60] hover:text-[#e8e8f0]'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5 inline mr-2" />
                Chats
              </button>
              <button
                onClick={() => setHistoryTab('projects')}
                className={`flex-1 py-3 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                  historyTab === 'projects' 
                    ? 'text-[#00d4ff] border-b-2 border-[#00d4ff]' 
                    : 'text-[#4a4a60] hover:text-[#e8e8f0]'
                }`}
              >
                <Folder className="w-3.5 h-3.5 inline mr-2" />
                Projects
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {historyTab === 'chats' && (
                <div className="space-y-2">
                  {chatSessions.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="w-8 h-8 text-[#2a2a3a] mx-auto mb-3" />
                      <p className="text-[#4a4a60] text-xs">No chat history yet</p>
                    </div>
                  ) : (
                    chatSessions.map((session) => (
                      <div
                        key={session.id}
                        onClick={() => loadChatSession(session)}
                        className={`bg-[#0d0d12] border p-3 cursor-pointer hover:border-[#00d4ff]/30 transition-colors ${
                          currentSessionId === session.id ? 'border-[#00d4ff]/50' : 'border-[#1a1a24]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-mono text-xs text-[#e8e8f0] truncate">{session.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] text-[#4a4a60]">{session.model || 'General'}</span>
                              {session.projectId && (
                                <span className="text-[9px] text-[#00d4ff] bg-[#00d4ff]/10 px-1.5 py-0.5">
                                  {projects.find(p => p.id === session.projectId)?.name || 'Project'}
                                </span>
                              )}
                            </div>
                            <p className="text-[9px] text-[#4a4a60] mt-1">
                              {new Date(session.updatedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <select
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => assignChatToProject(session.id, e.target.value || null)}
                              value={session.projectId || ''}
                              className="bg-[#08080c] border border-[#1a1a24] text-[9px] text-[#4a4a60] px-1 py-1 cursor-pointer"
                            >
                              <option value="">No Project</option>
                              {projects.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={(e) => deleteChatSession(session.id, e)}
                              className="p-1.5 hover:bg-[#ff3366]/10 text-[#4a4a60] hover:text-[#ff3366]"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {historyTab === 'projects' && (
                <div className="space-y-2">
                  {/* New Project Button */}
                  {showNewProject ? (
                    <div className="bg-[#0d0d12] border border-[#00d4ff]/30 p-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newProjectName}
                          onChange={(e) => setNewProjectName(e.target.value)}
                          placeholder="Project name..."
                          className="flex-1 bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono focus:border-[#00d4ff]/50 outline-none"
                          autoFocus
                        />
                        <button
                          onClick={createProject}
                          className="px-3 py-2 bg-[#00d4ff]/10 border border-[#00d4ff]/50 text-[#00d4ff] text-xs"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setShowNewProject(false); setNewProjectName(''); }}
                          className="px-3 py-2 border border-[#1a1a24] text-[#4a4a60] text-xs"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowNewProject(true)}
                      className="w-full border border-dashed border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-3 hover:border-[#00d4ff]/50 hover:text-[#00d4ff] transition-colors flex items-center justify-center gap-2"
                    >
                      <FolderPlus className="w-3.5 h-3.5" />
                      New Project
                    </button>
                  )}

                  {/* Projects List */}
                  {projects.length === 0 && !showNewProject ? (
                    <div className="text-center py-12">
                      <Folder className="w-8 h-8 text-[#2a2a3a] mx-auto mb-3" />
                      <p className="text-[#4a4a60] text-xs">No projects yet</p>
                      <p className="text-[#4a4a60] text-[10px] mt-1">Create a project to organize your chats</p>
                    </div>
                  ) : (
                    projects.map((project) => {
                      const projectChats = getProjectChats(project.id);
                      return (
                        <div
                          key={project.id}
                          className="bg-[#0d0d12] border border-[#1a1a24] p-3"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Folder className="w-4 h-4 text-[#00d4ff]" />
                              <span className="font-mono text-xs text-[#e8e8f0]">{project.name}</span>
                              <span className="text-[9px] text-[#4a4a60]">({projectChats.length} chats)</span>
                            </div>
                            <button
                              onClick={(e) => deleteProject(project.id, e)}
                              className="p-1.5 hover:bg-[#ff3366]/10 text-[#4a4a60] hover:text-[#ff3366]"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {projectChats.length > 0 && (
                            <div className="space-y-1 mt-2 pl-6 border-l border-[#1a1a24]">
                              {projectChats.slice(0, 5).map((chat) => (
                                <button
                                  key={chat.id}
                                  onClick={() => loadChatSession(chat)}
                                  className="w-full text-left text-[10px] text-[#4a4a60] hover:text-[#e8e8f0] py-1 truncate"
                                >
                                  {chat.title}
                                </button>
                              ))}
                              {projectChats.length > 5 && (
                                <p className="text-[9px] text-[#4a4a60]">+{projectChats.length - 5} more</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Model Picker Dropdown */}
      {showModelPicker && (
        <div className="absolute top-16 left-4 right-4 z-40 bg-[#0d0d12] border border-[#1a1a24] shadow-xl max-h-80 overflow-y-auto" data-testid="model-picker-dropdown">
          <div className="p-2 border-b border-[#1a1a24]">
            <p className="font-mono text-[9px] text-[#4a4a60] uppercase tracking-wider">Select AI Model</p>
          </div>
          {models.map((model) => (
            <div
              key={model.id}
              className={`flex items-start gap-3 p-3 hover:bg-[#111118] transition-colors ${
                selectedModel?.id === model.id ? 'bg-[#00d4ff]/5 border-l-2 border-[#00d4ff]' : ''
              }`}
            >
              <button
                onClick={() => {
                  setSelectedModel(model);
                  setShowModelPicker(false);
                  if (model.id !== selectedModel?.id) {
                    setMessages(prev => [...prev, {
                      role: 'assistant',
                      content: `Switched to **${model.name}** mode. ${model.description}`
                    }]);
                  }
                }}
                className="flex items-center gap-3 flex-1 text-left"
                data-testid={`model-option-${model.id}`}
              >
                <div className="w-9 h-9 border border-[#00d4ff]/20 rounded-lg flex items-center justify-center bg-[#00d4ff]/5 flex-shrink-0">
                  <ModelIcon model={model} size="sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[#00d4ff]">{model.name}</span>
                    {selectedModel?.id === model.id && (
                      <Check className="w-3 h-3 text-[#00d4ff]" />
                    )}
                  </div>
                  <p className="text-[10px] text-[#4a4a60] mt-0.5 line-clamp-2">{model.description}</p>
                </div>
              </button>
              
              {/* Premium Toggle Switch */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPremiumModels(prev => ({
                    ...prev,
                    [model.id]: !prev[model.id]
                  }));
                }}
                className="flex items-center gap-1 px-1 py-1 rounded-full transition-all"
                title={premiumModels[model.id] ? 'Using Gemini (Premium)' : 'Using Rhino (Free) - Click for Premium'}
                data-testid={`premium-toggle-${model.id}`}
              >
                {/* Toggle Track */}
                <div className={`relative w-14 h-6 rounded-full transition-all ${
                  premiumModels[model.id]
                    ? 'bg-gradient-to-r from-[#ffd700]/30 to-[#ff8c00]/30 border border-[#ffd700]/50'
                    : 'bg-[#1a1a24] border border-[#2a2a3a]'
                }`}>
                  {/* Labels */}
                  <span className={`absolute left-1.5 top-1/2 -translate-y-1/2 text-[7px] font-mono uppercase transition-opacity ${
                    premiumModels[model.id] ? 'opacity-0' : 'opacity-100 text-[#4a4a60]'
                  }`}>Free</span>
                  <span className={`absolute right-1 top-1/2 -translate-y-1/2 text-[7px] font-mono uppercase transition-opacity ${
                    premiumModels[model.id] ? 'opacity-100 text-[#ffd700]' : 'opacity-0'
                  }`}>Pro</span>
                  
                  {/* Toggle Knob */}
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                    premiumModels[model.id]
                      ? 'left-[calc(100%-22px)] bg-gradient-to-br from-[#ffd700] to-[#ff8c00] shadow-[0_0_8px_rgba(255,215,0,0.5)]'
                      : 'left-0.5 bg-[#4a4a60]'
                  }`}>
                    <Sparkles className={`w-2.5 h-2.5 ${premiumModels[model.id] ? 'text-[#08080c]' : 'text-[#2a2a3a]'}`} />
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Click outside to close model picker */}
      {showModelPicker && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => setShowModelPicker(false)}
        />
      )}

      {/* Add Skill Modal */}
      {showAddSkill && (
        <div className="absolute inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0d0d12] border border-[#1a1a24] w-full max-w-sm p-4" data-testid="add-skill-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#00d4ff]" />
                <span className="font-mono text-xs text-[#00d4ff] uppercase tracking-wider">Add Skill</span>
              </div>
              <button onClick={() => setShowAddSkill(false)} className="p-1 hover:bg-[#111118]">
                <X className="w-4 h-4 text-[#4a4a60]" />
              </button>
            </div>
            
            <input
              type="text"
              value={newSkill.title}
              onChange={(e) => setNewSkill({ ...newSkill, title: e.target.value })}
              placeholder="Skill name (e.g., 'My work schedule')"
              className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono mb-3 focus:border-[#00d4ff]/50 outline-none"
              data-testid="skill-title-input"
            />
            
            <textarea
              value={newSkill.content}
              onChange={(e) => setNewSkill({ ...newSkill, content: e.target.value })}
              placeholder="Describe the skill or knowledge (e.g., 'I work Monday-Friday, 9am-5pm')"
              className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono mb-3 focus:border-[#00d4ff]/50 outline-none resize-none"
              rows={4}
              data-testid="skill-content-input"
            />
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowAddSkill(false)}
                className="flex-1 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2 hover:bg-[#111118]"
              >
                Cancel
              </button>
              <button
                onClick={addSkill}
                disabled={!newSkill.title.trim() || !newSkill.content.trim()}
                className="flex-1 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2 hover:bg-[#00d4ff]/10 disabled:opacity-30"
                data-testid="save-skill-btn"
              >
                Save Skill
              </button>
            </div>
            
            <p className="text-[9px] text-[#4a4a60] mt-3 font-mono">
              Tip: You can also type "teach Sage that..." or "remember that..." in chat
            </p>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-32" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <div className="w-12 h-12 border border-[#1a1a24] mx-auto mb-4 flex items-center justify-center">
              <Bot className="w-6 h-6 text-[#1a1a24]" />
            </div>
            <p className="text-[#4a4a60] text-xs font-mono mb-2">Start a conversation</p>
            <p className="text-[#4a4a60] text-[10px]">
              Try: "teach Sage that I prefer concise answers"
            </p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] p-3 ${
                msg.role === 'user'
                  ? 'bg-[#00d4ff]/10 border border-[#00d4ff]/20'
                  : 'bg-[#0d0d12] border border-[#1a1a24]'
              }`}
              data-testid={`chat-message-${msg.role}`}
            >
              {/* Show image if present in message */}
              {msg.image && (
                <img 
                  src={msg.image} 
                  alt="Item" 
                  className="max-w-full h-auto rounded mb-2 border border-[#1a1a24]"
                />
              )}
              <div
                className="text-xs text-[#e8e8f0] whitespace-pre-wrap leading-relaxed"
                dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
              />
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#0d0d12] border border-[#1a1a24] p-3">
              <div className="flex gap-1.5">
                <span className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full animate-pulse"></span>
                <span className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></span>
                <span className="w-1.5 h-1.5 bg-[#00d4ff] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></span>
              </div>
            </div>
          </div>
        )}

        {/* Save to Prompt Vault - shown after Prompting Expert response */}
        {promptToSave && (
          <div className="flex justify-start">
            <div className="bg-[#0d0d12] border border-[#00d4ff]/30 p-3 max-w-[85%]" data-testid="prompt-save-prompt">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#00d4ff]" />
                <span className="text-xs text-[#00d4ff] font-mono">Save this prompt to your Vault?</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => saveToPromptVault(promptToSave)}
                  className="flex-1 bg-[#00d4ff]/10 border border-[#00d4ff]/50 text-[#00d4ff] font-mono text-[10px] uppercase py-2 px-4 hover:bg-[#00d4ff]/20 transition-colors"
                  data-testid="save-prompt-yes-btn"
                >
                  Yes, Save
                </button>
                <button
                  onClick={dismissPromptSave}
                  className="flex-1 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2 px-4 hover:bg-[#111118] transition-colors"
                  data-testid="save-prompt-no-btn"
                >
                  No Thanks
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reseller Camera Prompt - shown when General AI detects resale topic */}
        {showResellerPrompt && (
          <div className="flex justify-start">
            <div className="bg-[#0d0d12] border border-[#00ff88]/30 p-3 max-w-[85%]" data-testid="reseller-camera-prompt">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-4 h-4 text-[#00ff88]" />
                <span className="text-xs text-[#00ff88] font-mono">Want to snap a photo of the item?</span>
              </div>
              <p className="text-[10px] text-[#8888aa] mb-3">
                I can analyze a photo to give you accurate pricing and resale info.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={activateResellerCamera}
                  className="flex-1 bg-[#00ff88]/10 border border-[#00ff88]/50 text-[#00ff88] font-mono text-[10px] uppercase py-2 px-4 hover:bg-[#00ff88]/20 transition-colors flex items-center justify-center gap-2"
                  data-testid="activate-camera-btn"
                >
                  <Camera className="w-3 h-3" />
                  Yes, Open Camera
                </button>
                <button
                  onClick={dismissResellerPrompt}
                  className="flex-1 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2 px-4 hover:bg-[#111118] transition-colors"
                  data-testid="dismiss-camera-btn"
                >
                  No, Just Text
                </button>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Image Capture Modal - for Reseller Mode */}
      {showImageCapture && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="image-capture-modal">
          <div className="flex items-center justify-between p-4 border-b border-[#1a1a24]">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#00d4ff]" />
              <span className="font-mono text-sm text-[#00d4ff]">Capture Item</span>
            </div>
            <button onClick={closeImageCapture} className="p-2 hover:bg-[#111118]">
              <X className="w-5 h-5 text-[#4a4a60]" />
            </button>
          </div>
          
          <div className="flex-1 flex flex-col items-center justify-center p-4">
            {cameraError && (
              <div className="text-center mb-4 p-4 border border-[#ff3366]/30 bg-[#ff3366]/10">
                <p className="text-[#ff3366] text-xs font-mono">{cameraError}</p>
              </div>
            )}
            
            {isUsingCamera ? (
              <div className="relative w-full max-w-md">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  className="w-full rounded border border-[#1a1a24]"
                />
                <canvas ref={canvasRef} className="hidden" />
                <button
                  onClick={capturePhoto}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-[#00d4ff] flex items-center justify-center hover:bg-[#00d4ff]/80 transition-colors"
                  data-testid="capture-photo-btn"
                >
                  <Camera className="w-6 h-6 text-[#08080c]" />
                </button>
              </div>
            ) : (
              <div className="w-full max-w-md space-y-4">
                <button
                  onClick={startCamera}
                  className="w-full p-6 border-2 border-dashed border-[#00d4ff]/30 hover:border-[#00d4ff]/60 transition-colors flex flex-col items-center gap-3"
                  data-testid="start-camera-btn"
                >
                  <Camera className="w-10 h-10 text-[#00d4ff]" />
                  <span className="font-mono text-sm text-[#00d4ff]">Use Camera</span>
                  <span className="text-[10px] text-[#4a4a60]">Take a photo of the item</span>
                </button>
                
                <div className="text-center text-[#4a4a60] text-xs font-mono">or</div>
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full p-6 border-2 border-dashed border-[#1a1a24] hover:border-[#00d4ff]/30 transition-colors flex flex-col items-center gap-3"
                  data-testid="upload-image-btn"
                >
                  <Upload className="w-10 h-10 text-[#4a4a60]" />
                  <span className="font-mono text-sm text-[#e8e8f0]">Upload Image</span>
                  <span className="text-[10px] text-[#4a4a60]">Select from your device</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            )}
          </div>
          
          {isUsingCamera && (
            <div className="p-4 border-t border-[#1a1a24]">
              <button
                onClick={stopCamera}
                className="w-full border border-[#1a1a24] text-[#4a4a60] font-mono text-xs uppercase py-3 hover:bg-[#111118]"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Input */}
      <div className="fixed bottom-14 left-0 right-0 p-4 bg-[#08080c] border-t border-[#1a1a24]">
        <div className="max-w-md mx-auto">
          {/* Captured Image Preview */}
          {capturedImage && (
            <div className="mb-3 relative inline-block">
              <img 
                src={capturedImage} 
                alt="Captured item" 
                className="h-20 rounded border border-[#00d4ff]/30"
              />
              <button
                onClick={clearImage}
                className="absolute -top-2 -right-2 w-5 h-5 bg-[#ff3366] rounded-full flex items-center justify-center"
                data-testid="clear-image-btn"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          )}
          
          {/* Reseller Mode Indicator */}
          {isResellerMode && !capturedImage && (
            <div className="mb-3 p-2 bg-[#00d4ff]/5 border border-[#00d4ff]/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-[#00d4ff]" />
                <span className="text-[10px] text-[#00d4ff] font-mono">Reseller Mode: Snap or upload item photos for instant pricing</span>
              </div>
              <button
                onClick={openImageCapture}
                className="px-3 py-1 bg-[#00d4ff]/20 text-[#00d4ff] font-mono text-[10px] uppercase hover:bg-[#00d4ff]/30 transition-colors"
                data-testid="open-image-capture-btn"
              >
                Add Photo
              </button>
            </div>
          )}
          
          <div className="flex gap-2">
            {/* Camera Button - Only in Reseller Mode */}
            {isResellerMode && (
              <button
                onClick={openImageCapture}
                className={`border p-3 transition-all ${
                  capturedImage 
                    ? 'border-[#00ff88]/50 text-[#00ff88] bg-[#00ff88]/10' 
                    : 'border-[#1a1a24] text-[#4a4a60] hover:border-[#00d4ff]/30 hover:text-[#00d4ff]'
                }`}
                title="Capture or upload item image"
                data-testid="camera-btn"
              >
                {capturedImage ? <Image className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
              </button>
            )}
            
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isResellerMode ? "Describe the item or ask for pricing..." : "Message Sage or 'teach Sage that...'"}
              className="flex-1 bg-[#0d0d12] border border-[#00d4ff]/50 text-[#e8e8f0] px-4 py-3 text-xs font-mono focus:border-[#00d4ff] focus:shadow-[0_0_10px_rgba(0,212,255,0.2)] transition-all outline-none"
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              onClick={handleSend}
              disabled={(!input.trim() && !capturedImage) || loading}
              className="bg-[#00d4ff] text-[#000] p-3 transition-all hover:bg-[#00e5ff] disabled:bg-[#00d4ff]/40 disabled:text-[#000]/50"
              style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.3)' }}
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
